import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PrimaryCustomer from "@shared/PrimaryCustomer";
import SelectMergeCustomers from "@shared/SelectMergeCustomers";
import MergeCustomerFilter from "@shared/MergeCustomerFilter";
import CustomersToMerge from "@shared/CustomersToMerge";
import {
  Button,
  Grid,
  Header,
  Input,
  Modal,
} from "semantic-ui-react";
import MergeCustomersModalAction from "@common/redux/actions/MergeCustomersModalAction";
import CustomerClient from "@common/api/CustomerClient";
import { handleError } from "@common/components/helpers/error";
import LoadingAction from "@common/redux/actions/LoadingAction";

const STEP_SELECT = 1;
const STEP_CONFIRM = 2;
const STEP_SUCCESS = 3;

function customerDisplayName(customer) {
  if (!customer) return "";
  return `${customer?.customer?.forename} ${customer?.customer?.surname}`.trim();
}

const MergeCustomersModal = () => {
  const { open, primaryCustomer, onMergeComplete } = useSelector((state) =>
    MergeCustomersModalAction.getStatePath(state),
  );
  const [step, setStep] = useState(STEP_SELECT);
  const [selectedToMerge, setSelectedToMerge] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [mergeSuccessCount, setMergeSuccessCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [filterNameOrId, setFilterNameOrId] = useState("");
  const [filterSiteId, setFilterSiteId] = useState(null);
  const [filterAgeRangeId, setFilterAgeRangeId] = useState(null);
  const [filterEthnicityId, setFilterEthnicityId] = useState(null);
  const [filterGenderId, setFilterGenderId] = useState(null);
  const [searchMeta, setSearchMeta] = useState({
    ageRanges: [],
    ethnicity: [],
    genders: [],
    sites: [],
  });

  const primaryId = primaryCustomer?.customer?.id;

  const pageSize = 20;

  const buildSearchPayload = useCallback(
    (pageNum) => {
      const query = (filterNameOrId || "").trim();
      const payload = {
        pageNumber: pageNum,
        pageSize,
        ageRangeId: filterAgeRangeId || null,
        ethnicityId: filterEthnicityId || null,
        genderId: filterGenderId || null,
        siteId: filterSiteId || null,
        ...(primaryId != null && { customerId: primaryId }),
      };
      if (query) {
        if (!isNaN(query)) payload.id = parseInt(query, 10);
        else payload.name = query;
      }
      return payload;
    },
    [
      filterNameOrId,
      filterSiteId,
      filterAgeRangeId,
      filterEthnicityId,
      filterGenderId,
      primaryId,
    ],
  );

  const loadCustomers = useCallback(
    (pageNum = 1) => {
      setSearchLoading(true);
      CustomerClient.Search(buildSearchPayload(pageNum))
        .then((res) => {
          setSearchResults(res?.data || []);
          const total = res?.totalRecords ?? 0;
          setPageCount(Math.max(1, Math.ceil(total / pageSize)));
        })
        .catch(handleError)
        .finally(() => setSearchLoading(false));
    },
    [buildSearchPayload, pageSize],
  );

  useEffect(() => {
    if (open) loadCustomers(pageNumber);
  }, [open, pageNumber]);

  useEffect(() => {
    if (!open) return;
    CustomerClient.SearchMetaData()
      .then((res) => {
        setSearchMeta({
          ageRanges: res?.ageRanges ?? [],
          ethnicity: res?.ethnicity ?? [],
          genders: res?.genders ?? [],
          sites: res?.sites ?? [],
        });
      })
      .catch(handleError);
  }, [open]);

  const clear = useCallback(() => {
    setStep(STEP_SELECT);
    setSelectedToMerge([]);
    setFilterNameOrId("");
    setFilterSiteId(null);
    setFilterAgeRangeId(null);
    setFilterEthnicityId(null);
    setFilterGenderId(null);
    setSearchResults([]);
    setConfirmText("");
    setMergeSuccessCount(0);
    setPageNumber(1);
    setPageCount(1);
    loadCustomers();
  }, []);
  
  const close = useCallback(() => {
    clear();
    MergeCustomersModalAction.close();
  }, []);

  const runSearch = () => {
    setPageNumber(1);
    loadCustomers(1);
  };

  const toggleSelected = (customer, add) => {
    const id = customer?.id;
    if (add) {
      setSelectedToMerge((prev) =>
        prev.some((c) => c?.id === id)
          ? prev
          : [...prev, customer],
      );
    } else {
      setSelectedToMerge((prev) => prev.filter((c) => c?.id !== id));
    }
  };

  const isSelected = (customer) => {
    const id = customer?.id;
    return selectedToMerge.some((c) => c?.id === id);
  };

  const goToConfirm = () => setStep(STEP_CONFIRM);
  const goBackToSelect = () => setStep(STEP_SELECT);
  const confirmMerge = () => {
    if (!primaryId || selectedToMerge.length === 0) return;
    const duplicateCustomerIds = selectedToMerge.map((c) => c.id);
    LoadingAction.set(true);
    CustomerClient.Merge({ customerId: primaryId, duplicateCustomerIds })
      .then(() => {
        setMergeSuccessCount(duplicateCustomerIds.length);
        setStep(STEP_SUCCESS);
      })
      .catch(handleError)
      .finally(() => {
        LoadingAction.set(false)
        setConfirmText(null);
      });
  };

  const viewCustomers = () => {
    setStep(STEP_SELECT);
    setSelectedToMerge([]);
    setMergeSuccessCount(0);
    setPageNumber(1);
    loadCustomers(1);
  };

  const confirmEnabled = confirmText?.trim()?.toLowerCase() === "merge";
  const mergeButtonEnabled = selectedToMerge.length >= 1;
  const clearButtonEnabled = selectedToMerge.length > 0;

  if (!open) return null;

  const primaryName = customerDisplayName(primaryCustomer);

  return (
    <Modal open={open} onClose={close} closeIcon size="large">
      {step === STEP_SELECT && (
        <>
          <Modal.Header>Merge Customers</Modal.Header>
          <Modal.Content>
            <Grid >
              <Grid.Column width={5}>
                <PrimaryCustomer primaryCustomer={primaryCustomer} />
              </Grid.Column>
              <Grid.Column width={11}>
                <SelectMergeCustomers 
                  selectedToMerge={selectedToMerge} 
                  toggleSelected={toggleSelected} 
                  isSelected={isSelected} 
                  clear={clear} 
                  clearButtonEnabled={clearButtonEnabled} 
                  mergeButtonEnabled={mergeButtonEnabled} 
                  goToConfirm={goToConfirm} />
              </Grid.Column>
            </Grid>
            <MergeCustomerFilter 
              filterNameOrId={filterNameOrId} 
              setFilterNameOrId={setFilterNameOrId} 
              filterSiteId={filterSiteId} 
              setFilterSiteId={setFilterSiteId} 
              filterAgeRangeId={filterAgeRangeId} 
              setFilterAgeRangeId={setFilterAgeRangeId} 
              filterEthnicityId={filterEthnicityId} 
              setFilterEthnicityId={setFilterEthnicityId} 
              filterGenderId={filterGenderId} 
              setFilterGenderId={setFilterGenderId} 
              searchLoading={searchLoading} 
              runSearch={runSearch} 
              searchMeta={searchMeta}
            />
            <CustomersToMerge
              customersToMerge={searchResults}
              pageNumber={pageNumber}
              pageCount={pageCount}
              setPageNumber={setPageNumber} 
              isSelected={isSelected} 
              toggleSelected={toggleSelected} 
            />
          </Modal.Content>
          <Modal.Actions>
            
          </Modal.Actions>
        </>
      )}

      {step === STEP_CONFIRM && (
        <>
          <Modal.Header>Merge Customers - Confirmation Required</Modal.Header>
          <Modal.Content>
            <p className="text-danger font-weight-bold">
              WARNING: THIS ACTION CANNOT BE UNDONE.
            </p>
            <p className="font-bold">
              You are about to merge the {selectedToMerge.length} selected
              customer(s) into Primary Customer - {primaryName}.
            </p>
            <ul>
              <li>
                All compliance records recorded against these customers will be reassigned to the Primary Customer.
              </li>
              <li>
                The {selectedToMerge.length} selected merge customers&apos;
                personal information will be PERMANENTLY DELETED.
              </li>
              <li>
                  If the both the primary customer and merged customers have pending reviews outstanding, the merged customers&apos; 
                  review will be DELETED.
              </li>
            </ul>
            <Grid >
              <Grid.Column width={5}>
                <PrimaryCustomer primaryCustomer={primaryCustomer} />
              </Grid.Column>
              <Grid.Column width={11}>
                <SelectMergeCustomers 
                    selectedToMerge={selectedToMerge} 
                    toggleSelected={toggleSelected} 
                    isSelected={isSelected} 
                    clear={clear} 
                    clearButtonEnabled={clearButtonEnabled} 
                    mergeButtonEnabled={mergeButtonEnabled} 
                    goToConfirm={goToConfirm} 
                    disabled={true}
                />
              </Grid.Column>
            </Grid>
            <p className="font-bold">To confirm, please type &quot;merge&quot; in the box below:</p>
            <Input
              placeholder='Type "merge"'
              value={confirmText}
              onChange={(_, { value }) => setConfirmText(value)}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={goBackToSelect}>
              Back
            </Button>
            <Button
              color="green"
              disabled={!confirmEnabled}
              onClick={confirmMerge}
            >
              Confirm
            </Button>
          </Modal.Actions>
        </>
      )}

      {step === STEP_SUCCESS && (
        <>
          <Modal.Header>Merge Customers Complete</Modal.Header>
          <Modal.Content>
            <Header as="h5">Success:</Header>
            <ul>
              <li>
                {mergeSuccessCount} record(s) merged into <strong>{primaryName}</strong>
              </li>
              <li>Merged customers&apos; profiles deleted.</li>
            </ul>
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={close}>
              Back
            </Button>
            <Button color="green" onClick={viewCustomers}>
              View Customers
            </Button>
          </Modal.Actions>
        </>
      )}
    </Modal>
  );
};

export default MergeCustomersModal;
